import React, { useState, useEffect } from 'react';
import {
  Drawer,
  Slider,
  ColorPicker,
  Typography,
  Divider,
  Space,
  InputNumber,
  Row,
  Col,
} from 'antd';
import { ASSET_CATEGORY_TYPES } from 'src/types/assetTypes';
import * as THREE from 'three';

const { Title, Text } = Typography;

interface ShaderPropertiesDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  selectedModel: any;
  onUpdateShaderProperties: (properties: any) => void;
}

interface WaterShaderProperties {
  wavesAmplitude: number;
  wavesFrequency: number;
  wavesPersistence: number;
  wavesLacunarity: number;
  wavesIterations: number;
  wavesSpeed: number;

  opacity: number;

  troughColor: string;
  surfaceColor: string;
  peakColor: string;

  peakThreshold: number;
  peakTransition: number;
  troughThreshold: number;
  troughTransition: number;

  fresnelScale: number;
  fresnelPower: number;
}

const defaultWaterProperties: WaterShaderProperties = {
  wavesAmplitude: 0.025,
  wavesFrequency: 1.07,
  wavesPersistence: 0.3,
  wavesLacunarity: 2.18,
  wavesIterations: 8,
  wavesSpeed: 0.4,
  opacity: 0.8,
  troughColor: '#186691',
  surfaceColor: '#9bd8c0',
  peakColor: '#bbd8e0',
  peakThreshold: 0.08,
  peakTransition: 0.05,
  troughThreshold: -0.01,
  troughTransition: 0.15,
  fresnelScale: 0.8,
  fresnelPower: 0.5,
};

const ShaderPropertiesDrawer: React.FC<ShaderPropertiesDrawerProps> = ({
  isOpen,
  onClose,
  selectedModel,
  onUpdateShaderProperties,
}) => {
  const [properties, setProperties] = useState<WaterShaderProperties>(
    defaultWaterProperties
  );

  const isWaterShader =
    selectedModel?.props?.asset?.type === ASSET_CATEGORY_TYPES.WATER_SHADER;

  useEffect(() => {
    if (isWaterShader && selectedModel?.props?.asset?.metadata) {
      const metadata = selectedModel.props.asset.metadata;
      const shaderParams = metadata.shaderParams || {};
      setProperties({
        wavesAmplitude:
          shaderParams.wavesAmplitude ?? defaultWaterProperties.wavesAmplitude,
        wavesFrequency:
          shaderParams.wavesFrequency ?? defaultWaterProperties.wavesFrequency,
        wavesPersistence:
          shaderParams.wavesPersistence ??
          defaultWaterProperties.wavesPersistence,
        wavesLacunarity:
          shaderParams.wavesLacunarity ??
          defaultWaterProperties.wavesLacunarity,
        wavesIterations:
          shaderParams.wavesIterations ??
          defaultWaterProperties.wavesIterations,
        wavesSpeed:
          shaderParams.wavesSpeed ?? defaultWaterProperties.wavesSpeed,
        opacity: shaderParams.opacity ?? defaultWaterProperties.opacity,
        troughColor:
          shaderParams.troughColor ?? defaultWaterProperties.troughColor,
        surfaceColor:
          shaderParams.surfaceColor ?? defaultWaterProperties.surfaceColor,
        peakColor: shaderParams.peakColor ?? defaultWaterProperties.peakColor,
        peakThreshold:
          shaderParams.peakThreshold ?? defaultWaterProperties.peakThreshold,
        peakTransition:
          shaderParams.peakTransition ?? defaultWaterProperties.peakTransition,
        troughThreshold:
          shaderParams.troughThreshold ??
          defaultWaterProperties.troughThreshold,
        troughTransition:
          shaderParams.troughTransition ??
          defaultWaterProperties.troughTransition,
        fresnelScale:
          shaderParams.fresnelScale ?? defaultWaterProperties.fresnelScale,
        fresnelPower:
          shaderParams.fresnelPower ?? defaultWaterProperties.fresnelPower,
      });
    }
  }, [selectedModel, isWaterShader]);

  const handlePropertyChange = (
    key: keyof WaterShaderProperties,
    value: any
  ) => {
    const newProperties = { ...properties, [key]: value };
    setProperties(newProperties);
    onUpdateShaderProperties(newProperties);
  };

  if (!isWaterShader) {
    return (
      <Drawer
        title='Shader Properties'
        placement='right'
        onClose={onClose}
        open={isOpen}
        width={400}
      >
        <Text>Please select a water shader to edit its properties.</Text>
      </Drawer>
    );
  }

  return (
    <Drawer
      title='Water Shader Properties'
      placement='right'
      onClose={onClose}
      open={isOpen}
      width={400}
    >
      <Space direction='vertical' style={{ width: '100%' }} size='large'>
        <div>
          <Title level={4}>Wave Properties</Title>

          <Space direction='vertical' style={{ width: '100%' }}>
            <div>
              <Text strong>
                Amplitude: {properties.wavesAmplitude.toFixed(3)}
              </Text>
              <Slider
                min={0}
                max={0.2}
                step={0.001}
                value={properties.wavesAmplitude}
                onChange={(value) =>
                  handlePropertyChange('wavesAmplitude', value)
                }
              />
            </div>

            <div>
              <Text strong>
                Frequency: {properties.wavesFrequency.toFixed(2)}
              </Text>
              <Slider
                min={0.1}
                max={5}
                step={0.01}
                value={properties.wavesFrequency}
                onChange={(value) =>
                  handlePropertyChange('wavesFrequency', value)
                }
              />
            </div>

            <div>
              <Text strong>Speed: {properties.wavesSpeed.toFixed(2)}</Text>
              <Slider
                min={0}
                max={2}
                step={0.01}
                value={properties.wavesSpeed}
                onChange={(value) => handlePropertyChange('wavesSpeed', value)}
              />
            </div>

            <div>
              <Text strong>
                Persistence: {properties.wavesPersistence.toFixed(2)}
              </Text>
              <Slider
                min={0}
                max={1}
                step={0.01}
                value={properties.wavesPersistence}
                onChange={(value) =>
                  handlePropertyChange('wavesPersistence', value)
                }
              />
            </div>

            <div>
              <Text strong>
                Lacunarity: {properties.wavesLacunarity.toFixed(2)}
              </Text>
              <Slider
                min={1}
                max={5}
                step={0.01}
                value={properties.wavesLacunarity}
                onChange={(value) =>
                  handlePropertyChange('wavesLacunarity', value)
                }
              />
            </div>

            <Row gutter={16} align='middle'>
              <Col span={12}>
                <Text strong>Iterations:</Text>
              </Col>
              <Col span={12}>
                <InputNumber
                  min={1}
                  max={16}
                  value={properties.wavesIterations}
                  onChange={(value) =>
                    handlePropertyChange('wavesIterations', value || 1)
                  }
                  style={{ width: '100%' }}
                />
              </Col>
            </Row>
          </Space>
        </div>

        <Divider />

        <div>
          <Title level={4}>Visual Properties</Title>

          <div>
            <Text strong>Opacity: {properties.opacity.toFixed(2)}</Text>
            <Slider
              min={0}
              max={1}
              step={0.01}
              value={properties.opacity}
              onChange={(value) => handlePropertyChange('opacity', value)}
            />
          </div>
        </div>

        <Divider />

        <div>
          <Title level={4}>Colors</Title>

          <Space direction='vertical' style={{ width: '100%' }}>
            <Row gutter={16} align='middle'>
              <Col span={12}>
                <Text strong>Trough Color:</Text>
              </Col>
              <Col span={12}>
                <ColorPicker
                  value={properties.troughColor}
                  onChange={(color) =>
                    handlePropertyChange('troughColor', color.toHexString())
                  }
                  showText
                />
              </Col>
            </Row>

            <Row gutter={16} align='middle'>
              <Col span={12}>
                <Text strong>Surface Color:</Text>
              </Col>
              <Col span={12}>
                <ColorPicker
                  value={properties.surfaceColor}
                  onChange={(color) =>
                    handlePropertyChange('surfaceColor', color.toHexString())
                  }
                  showText
                />
              </Col>
            </Row>

            <Row gutter={16} align='middle'>
              <Col span={12}>
                <Text strong>Peak Color:</Text>
              </Col>
              <Col span={12}>
                <ColorPicker
                  value={properties.peakColor}
                  onChange={(color) =>
                    handlePropertyChange('peakColor', color.toHexString())
                  }
                  showText
                />
              </Col>
            </Row>
          </Space>
        </div>

        <Divider />

        <div>
          <Title level={4}>Thresholds & Transitions</Title>

          <Space direction='vertical' style={{ width: '100%' }}>
            <div>
              <Text strong>
                Peak Threshold: {properties.peakThreshold.toFixed(3)}
              </Text>
              <Slider
                min={-0.1}
                max={0.2}
                step={0.001}
                value={properties.peakThreshold}
                onChange={(value) =>
                  handlePropertyChange('peakThreshold', value)
                }
              />
            </div>

            <div>
              <Text strong>
                Peak Transition: {properties.peakTransition.toFixed(3)}
              </Text>
              <Slider
                min={0}
                max={0.2}
                step={0.001}
                value={properties.peakTransition}
                onChange={(value) =>
                  handlePropertyChange('peakTransition', value)
                }
              />
            </div>

            <div>
              <Text strong>
                Trough Threshold: {properties.troughThreshold.toFixed(3)}
              </Text>
              <Slider
                min={-0.1}
                max={0.1}
                step={0.001}
                value={properties.troughThreshold}
                onChange={(value) =>
                  handlePropertyChange('troughThreshold', value)
                }
              />
            </div>

            <div>
              <Text strong>
                Trough Transition: {properties.troughTransition.toFixed(3)}
              </Text>
              <Slider
                min={0}
                max={0.5}
                step={0.001}
                value={properties.troughTransition}
                onChange={(value) =>
                  handlePropertyChange('troughTransition', value)
                }
              />
            </div>
          </Space>
        </div>

        <Divider />

        <div>
          <Title level={4}>Fresnel Effect</Title>

          <Space direction='vertical' style={{ width: '100%' }}>
            <div>
              <Text strong>
                Fresnel Scale: {properties.fresnelScale.toFixed(2)}
              </Text>
              <Slider
                min={0}
                max={2}
                step={0.01}
                value={properties.fresnelScale}
                onChange={(value) =>
                  handlePropertyChange('fresnelScale', value)
                }
              />
            </div>

            <div>
              <Text strong>
                Fresnel Power: {properties.fresnelPower.toFixed(2)}
              </Text>
              <Slider
                min={0.1}
                max={5}
                step={0.01}
                value={properties.fresnelPower}
                onChange={(value) =>
                  handlePropertyChange('fresnelPower', value)
                }
              />
            </div>
          </Space>
        </div>
      </Space>
    </Drawer>
  );
};

export default ShaderPropertiesDrawer;
